/**
 * Route guard that gates a subtree on Cognito group membership.
 *
 * The component is purely a UX hint — the API enforces group-based
 * authorization on every request (Requirement 8.4, see `packages/api/src/lib/auth.ts`).
 * Hiding routes the user is not entitled to keeps the UI honest and avoids
 * making API calls we know will be rejected.
 *
 * Validates: Requirements 8.1, 8.4
 */

import type { ReactNode } from 'react';
import { hasAnyGroup, type IdTokenClaims } from './cognito';
import { useAuth } from './AuthContext';

interface RequireGroupProps {
  /** At least one of these groups must be present in the user's claims. */
  anyOf: readonly string[];
  /** Subtree shown when the guard passes. */
  children: ReactNode;
  /** Subtree shown when the user is signed in but lacks any required group. */
  fallback?: ReactNode;
  /** Subtree shown when the user is not signed in. */
  signedOutFallback?: ReactNode;
}

/**
 * Renders `children` only when the signed-in user has at least one of the
 * groups in `anyOf`. When unauthenticated, renders `signedOutFallback` (or
 * a default sign-in prompt). When authenticated but unauthorized, renders
 * `fallback` (or a default forbidden message).
 */
export function RequireGroup({
  anyOf,
  children,
  fallback,
  signedOutFallback,
}: RequireGroupProps): JSX.Element {
  const { claims, loading, signIn } = useAuth();

  if (loading) {
    return <p>Checking sign-in…</p>;
  }
  if (claims === null) {
    if (signedOutFallback !== undefined) {
      return <>{signedOutFallback}</>;
    }
    return (
      <div>
        <p>You need to sign in to view this page.</p>
        <button type="button" onClick={() => void signIn()}>
          Sign in
        </button>
      </div>
    );
  }
  if (!isAllowed(claims, anyOf)) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div>
        <p>This page requires one of: {anyOf.join(', ')}.</p>
        <p>Your account does not have the required role.</p>
      </div>
    );
  }
  return <>{children}</>;
}

/** Pure helper used by both the component and unit tests. */
export function isAllowed(claims: IdTokenClaims | null, anyOf: readonly string[]): boolean {
  if (claims === null) return false;
  if (anyOf.length === 0) return true;
  return hasAnyGroup(claims, anyOf);
}
